import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "components/mui";
import { GetConsentStatusLog } from "api/canmail/CANmailAPI";

function ConsentLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchConsentLogs();
  }, []);

  const fetchConsentLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GetConsentStatusLog();

      if (data && typeof data === "string") {
        // Parse the JSON string response
        const parsedData = JSON.parse(data);
        // Add unique IDs for DataGrid
        const logsWithIds = parsedData.map((log, index) => ({
          id: index + 1,
          ...log,
        }));
        setLogs(logsWithIds);
      } else if (Array.isArray(data)) {
        // If data is already an array
        const logsWithIds = data.map((log, index) => ({
          id: index + 1,
          ...log,
        }));
        setLogs(logsWithIds);
      } else {
        setLogs([]);
      }
    } catch (err) {
      console.error("Error fetching consent logs:", err);
      setError("Failed to load consent logs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "contactName",
      headerName: "Contact Name",
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: "contactEmail",
      headerName: "Email",
      width: 250,
      filterable: true,
      sortable: true,
    },
    {
      field: "listName",
      headerName: "Mailing List",
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: "added_or_removed",
      headerName: "Action",
      width: 120,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Added" : "Removed"}
          color={params.value === 1 ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      width: 300,
      filterable: true,
      sortable: true,
    },
    {
      field: "date",
      headerName: "Date",
      width: 200,
      filterable: true,
      sortable: true,
      type: "dateTime",
      valueGetter: (params) => {
        // Convert the date string to a Date object for proper sorting
        return params.value ? new Date(params.value) : null;
      },
      valueFormatter: (params) => {
        // Format the date for display
        return params.value ? params.value.toLocaleString() : "";
      },
    },
  ];

  // Filter logs based on search term
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) {
      return logs;
    }

    const searchLower = searchTerm.toLowerCase();
    return logs.filter(
      (log) =>
        log.contactName?.toLowerCase().includes(searchLower) ||
        log.contactEmail?.toLowerCase().includes(searchLower) ||
        log.listName?.toLowerCase().includes(searchLower)
    );
  }, [logs, searchTerm]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Email Consent Log
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          View all email consent changes including additions and removals from
          mailing lists.
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name, email, or list..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 300,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
            },
          }}
        />
      </Box>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredLogs}
          columns={columns}
          initialState={{
            pagination: {
              pageSize: 25,
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          checkboxSelection={false}
          disableColumnMenu={false}
          filterMode="client"
          sortingMode="client"
          pagination
          paginationMode="client"
          density="standard"
          sx={{
            backgroundColor: "white",
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "white",
              borderBottom: "1px solid",
              borderBottomColor: "divider",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "white",
              borderTop: "1px solid",
              borderTopColor: "divider",
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "white",
            },
            "& .MuiDataGrid-cell": {
              backgroundColor: "white",
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default ConsentLog;
